'use strict';

/**
 * BlueprintLayout
 * ---------------
 * A composable blueprint layout as SEMANTIC REGIONS on a PROPORTIONAL GRID (F-071, Strand 2).
 *
 * The point of the whole feature is one authored layout that renders UNCHANGED to a shareable web page, a
 * Letter PDF and a 24x36 ARCH D sheet. That only holds if a region's place is stored relative to the
 * sheet, not in pixels: a region sits at a grid cell (column, row, span), and the renderer maps the grid
 * onto whatever dimensions the output has. Letter and ARCH D are then the same arrangement at two scales,
 * which is exactly what "do not change the content between sizes" means, made true by construction rather
 * than by a scaling pass bolted on after.
 *
 * Pure string building, no DOM and no Pict dependency, so the same render runs in the browser (the canvas
 * preview and the shared page) and on the server (the print path hands this HTML to headless Chromium).
 * This is the model the canvas editor edits and the three outputs read; it is deliberately the ONE place
 * a blueprint's arrangement lives, so the editor, the web page and the PDF cannot come to disagree.
 *
 *   A layout:
 *     { Grid: { Cols, Rows }, Regions: [ Region ] }
 *   A region:
 *     { Kind, Col, Row, ColSpan, RowSpan, ... kind-specific content }
 *
 *   Kinds:
 *     'title'    the sheet's title block: Name, Tagline, Mark
 *     'diagram'  an inline SVG (vector, scales to any size, inherits ink) -- the architecture drawing
 *     'blurbs'   a stack of titled prose blocks: [{ Heading, Body }]
 *     'image'    a raster or SVG figure (a visual example / hero), by data URI or media src, with Caption
 *     'note'     a small labelled caption block (a legend, a stat, a call-out)
 *
 *   renderLayout(layout, opts) -> the grid HTML
 *   LAYOUT_CSS                 -> the styles (scoped to .bpl)
 *   moduleOverview(spec)       -> a default module-overview layout from a small spec
 *
 * opts:
 *   Mode   'web' | 'print'  -- print sizes type in points off the sheet; web sizes in viewport units
 *   fIcon  optional icon resolver (unused today; kept so the canvas can pass the Pict icon set)
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

function _escape(pText)
{
	return String(pText == null ? '' : pText)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// A placement (Col/Row/ColSpan/RowSpan), clamped so a bad column or span cannot push it off the sheet, and
// returned as the two grid track strings. Used for a region's base placement and its portrait alternate alike.
function _cells(pPlacement, pGrid)
{
	let tmpCol = Math.max(1, Math.min(pGrid.Cols, Number(pPlacement.Col) || 1));
	let tmpRow = Math.max(1, Math.min(pGrid.Rows, Number(pPlacement.Row) || 1));
	let tmpColSpan = Math.max(1, Math.min(pGrid.Cols - (tmpCol - 1), Number(pPlacement.ColSpan) || 1));
	let tmpRowSpan = Math.max(1, Math.min(pGrid.Rows - (tmpRow - 1), Number(pPlacement.RowSpan) || 1));
	return { Col: tmpCol + ' / span ' + tmpColSpan, Row: tmpRow + ' / span ' + tmpRowSpan };
}

// A region's grid placement. A non-responsive layout keeps today's inline grid-column/grid-row verbatim, so
// its markup is byte-identical. A responsive layout emits placement as CSS custom properties instead: the
// real grid-column/grid-row declarations live in LAYOUT_CSS and read these vars, so a @container rule can
// re-place the region for a portrait container -- which an inline grid-column could not, because an inline
// declaration outranks any stylesheet rule. A region with no Portrait alternate keeps its base placement at
// every aspect (the LAYOUT_CSS var() fallback), so the alternate is strictly opt-in.
function _place(pRegion, pGrid, pResponsive)
{
	let tmpBase = _cells(pRegion, pGrid);
	if (!pResponsive) { return 'grid-column: ' + tmpBase.Col + '; grid-row: ' + tmpBase.Row + ';'; }
	let tmpStyle = '--bpl-gc: ' + tmpBase.Col + '; --bpl-gr: ' + tmpBase.Row + ';';
	if (pRegion.Portrait)
	{
		let tmpPortrait = _cells(pRegion.Portrait, pGrid);
		tmpStyle += ' --bpl-gc-p: ' + tmpPortrait.Col + '; --bpl-gr-p: ' + tmpPortrait.Row + ';';
	}
	return tmpStyle;
}

function _title(pRegion)
{
	return '<div class="bpl-title">'
		+ (pRegion.Mark ? ('<div class="bpl-title-mark">' + _escape(pRegion.Mark) + '</div>') : '')
		+ '<div class="bpl-title-name">' + _escape(pRegion.Name || '') + '</div>'
		+ (pRegion.Tagline ? ('<div class="bpl-title-tag">' + _escape(pRegion.Tagline) + '</div>') : '')
	+ '</div>';
}

function _blurbs(pRegion)
{
	let tmpItems = (pRegion.Items || []).map((pItem) =>
		'<div class="bpl-blurb">'
			+ (pItem.Heading ? ('<div class="bpl-blurb-h">' + _escape(pItem.Heading) + '</div>') : '')
			+ '<div class="bpl-blurb-b">' + _escape(pItem.Body || '') + '</div>'
		+ '</div>').join('');
	return '<div class="bpl-blurbs">' + tmpItems + '</div>';
}

// A diagram is inline SVG so it stays vector and inherits the sheet ink (currentColor). The SVG is trusted
// layout content authored in the app, not arbitrary remote markup; the prolog/doctype are stripped so it
// is valid inside HTML flow.
function _diagram(pRegion)
{
	let tmpSVG = String(pRegion.SVG || '').replace(/<\?xml[\s\S]*?\?>/i, '').replace(/<!DOCTYPE[^>]*>/i, '').trim();
	return '<figure class="bpl-diagram">' + tmpSVG
		+ (pRegion.Caption ? ('<figcaption>' + _escape(pRegion.Caption) + '</figcaption>') : '')
	+ '</figure>';
}

function _image(pRegion)
{
	let tmpSrc = pRegion.DataURI || pRegion.Src || '';
	let tmpImg = tmpSrc ? ('<img alt="' + _escape(pRegion.Alt || '') + '" src="' + _escape(tmpSrc) + '">') : '<div class="bpl-image-empty">image</div>';
	return '<figure class="bpl-image">' + tmpImg
		+ (pRegion.Caption ? ('<figcaption>' + _escape(pRegion.Caption) + '</figcaption>') : '')
	+ '</figure>';
}

function _note(pRegion)
{
	return '<div class="bpl-note">'
		+ (pRegion.Label ? ('<div class="bpl-note-l">' + _escape(pRegion.Label) + '</div>') : '')
		+ '<div class="bpl-note-b">' + _escape(pRegion.Body || '') + '</div>'
	+ '</div>';
}

function _region(pRegion, pGrid, pResponsive)
{
	let tmpInner;
	switch (pRegion.Kind)
	{
		case 'title': tmpInner = _title(pRegion); break;
		case 'blurbs': tmpInner = _blurbs(pRegion); break;
		case 'diagram': tmpInner = _diagram(pRegion); break;
		case 'image': tmpInner = _image(pRegion); break;
		case 'note': tmpInner = _note(pRegion); break;
		default: tmpInner = ''; break;
	}
	return '<div class="bpl-region bpl-region-' + _escape(pRegion.Kind || 'empty') + '" style="' + _place(pRegion, pGrid, pResponsive) + '">' + tmpInner + '</div>';
}

/**
 * The layout as one grid. The grid is relative (fr tracks), so the caller gives the OUTER box its dimensions
 * (an aspect-ratio for the web, fixed inches for the sheet) and the region type scale rides on --bpl-scale so
 * text and padding grow with the paper.
 *
 * A layout with Responsive:true opts into aspect-responsive placement: regions carry both a base (landscape)
 * placement and an optional Portrait alternate as CSS custom properties, and a @container query in LAYOUT_CSS
 * switches between them by the container's aspect ratio. Without the flag the markup is exactly as before.
 * pOptions.Responsive === false forces base placement regardless of the flag (the studio uses this so its
 * drag math, which reads the base placement, never desyncs from a portrait alternate).
 */
function renderLayout(pLayout, pOptions)
{
	let tmpLayout = pLayout || {};
	let tmpOptions = pOptions || {};
	let tmpGrid = tmpLayout.Grid || { Cols: 12, Rows: 16 };
	let tmpResponsive = (tmpOptions.Responsive === false) ? false : (tmpLayout.Responsive === true);
	let tmpRegions = (tmpLayout.Regions || []).map((pRegion) => _region(pRegion, tmpGrid, tmpResponsive)).join('');
	let tmpClass = tmpResponsive ? 'bpl bpl-responsive' : 'bpl';
	return '<div class="' + tmpClass + '" style="--bpl-cols: ' + tmpGrid.Cols + '; --bpl-rows: ' + tmpGrid.Rows + ';">' + tmpRegions + '</div>';
}

const LAYOUT_CSS = /*css*/`
.bpl { display: grid; grid-template-columns: repeat(var(--bpl-cols, 12), 1fr); grid-template-rows: repeat(var(--bpl-rows, 16), 1fr);
	gap: calc(0.9% * var(--bpl-scale, 1)); width: 100%; height: 100%; box-sizing: border-box; color: var(--bpl-ink, #12161d);
	font-family: "IBM Plex Sans", system-ui, sans-serif; container-type: size; }
.bpl-region { min-width: 0; min-height: 0; overflow: hidden; display: flex; flex-direction: column; }
/* Aspect-responsive placement (opt-in: only layouts rendered with Responsive:true carry .bpl-responsive).
   The one 12x16 grid of 1fr tracks reshapes to fill whatever box it is given, so only WHICH cells a region
   occupies changes between a wide (landscape) and a tall (portrait) container. Landscape is the base. Below
   1:1 a region switches to its Portrait alternate; a region with none falls back to its base via the nested
   var() default, so the alternate is strictly opt-in. The vars come from the region's inline style; these
   stylesheet declarations are what actually set grid placement, which an inline grid-column could not be
   overridden to do. Scoped under .bpl-responsive so a non-responsive layout (inline grid-column, no class)
   is untouched. Switched on .bpl-region, which resolves against .bpl -- the nearest size container on every
   surface, including the studio, which has no .bpl-sheet. */
.bpl-responsive .bpl-region { grid-column: var(--bpl-gc); grid-row: var(--bpl-gr); }
/* A responsive diagram binds to its grid cell (the cell is a definite height, so max-height on the SVG can
   finally resolve). Without this a wide-short cell -- e.g. a code banner stretched full-width on a landscape
   sheet -- lets the width:100% SVG overflow its row and the cell's overflow:hidden clips it. The SVG's own
   viewBox keeps the drawing centered and whole at whatever box the cell gives it. Scoped to responsive so a
   fixed blueprint's diagram sits exactly as before. */
.bpl-responsive .bpl-diagram { flex: 1 1 auto; min-height: 0; }
@container (aspect-ratio < 1 / 1)
{
	.bpl-responsive .bpl-region { grid-column: var(--bpl-gc-p, var(--bpl-gc)); grid-row: var(--bpl-gr-p, var(--bpl-gr)); }
}
/* Type sizes start in container-query units (cqh), so they scale with the sheet, and every text region
   carries a --bpl-fit multiplier that fitLayout() dials down until the content fits its cell. Text inside
   a text region is in em off a single scalable root, so one multiplier shrinks the whole block together --
   that is what keeps a blurb column from clipping into the figure below it at any size. */
.bpl-title { border-bottom: 0.25cqh solid var(--bpl-rule, #cbd2dd); padding-bottom: 1cqh; display: flex; flex-direction: column; gap: 0.5em; justify-content: flex-end; font-size: calc(2cqh * var(--bpl-fit, 1)); }
.bpl-title-mark { font-family: "Gorton Perfected", "IBM Plex Mono", monospace; font-size: 0.72em; letter-spacing: 0.08em; text-transform: uppercase; color: var(--bpl-quiet, #6b7280); }
.bpl-title-name { font-family: "Gorton Perfected", "IBM Plex Sans", sans-serif; font-weight: 700; font-size: 2.2em; line-height: 1.02; }
.bpl-title-tag { font-size: 0.95em; line-height: 1.3; color: var(--bpl-quiet, #566072); max-width: 62ch; }
.bpl-diagram { margin: 0; padding: 1.4cqh; display: flex; flex-direction: column; align-items: center; justify-content: center; border: 0.18cqh solid var(--bpl-rule, #d6dbe4); border-radius: 0.4cqh; background: var(--bpl-panel, #fbfcfe); }
.bpl-diagram svg { max-width: 100%; max-height: 100%; height: auto; color: var(--bpl-ink, #12161d); }
.bpl-diagram figcaption, .bpl-image figcaption { font-family: "Gorton Perfected", "IBM Plex Mono", monospace; font-size: 1.4cqh; text-transform: uppercase; letter-spacing: 0.05em; color: var(--bpl-quiet, #6b7280); margin-top: 0.8cqh; }
.bpl-blurbs { display: flex; flex-direction: column; gap: 0.9em; padding-left: 1.4cqh; font-size: calc(1.95cqh * var(--bpl-fit, 1)); }
.bpl-blurb-h { font-family: "Gorton Perfected", "IBM Plex Sans", sans-serif; font-weight: 700; font-size: 1.13em; margin-bottom: 0.2em; }
.bpl-blurb-b { font-size: 1em; line-height: 1.42; color: var(--bpl-body, #2b3340); }
.bpl-image { margin: 0; display: flex; flex-direction: column; }
.bpl-image img { width: 100%; height: 100%; object-fit: contain; border-radius: 0.3cqh; min-height: 0; }
.bpl-image-empty { flex: 1; display: flex; align-items: center; justify-content: center; border: 0.2cqh dashed var(--bpl-rule, #cbd2dd); color: var(--bpl-quiet, #9aa3b2); font-size: 2cqh; border-radius: 0.3cqh; }
.bpl-note { border-left: 0.3cqh solid var(--bpl-accent, #3b6fd4); padding-left: 1cqh; display: flex; flex-direction: column; gap: 0.25em; justify-content: center; font-size: calc(1.9cqh * var(--bpl-fit, 1)); }
.bpl-note-l { font-family: "Gorton Perfected", "IBM Plex Mono", monospace; font-size: 0.7em; text-transform: uppercase; letter-spacing: 0.06em; color: var(--bpl-quiet, #6b7280); }
.bpl-note-b { font-size: 1em; line-height: 1.35; color: var(--bpl-body, #2b3340); }
`;

/**
 * Shrink each text region's type until its content fits its grid cell.
 *
 * A grid cell is a fixed fraction of the sheet, but how much prose a region holds is the author's, so at a
 * smaller size (or with more words) a blurb column would overflow and clip into the figure below it. CSS
 * alone cannot fit arbitrary text to a box; this steps the region's --bpl-fit multiplier down until the
 * content stops overflowing (or hits a floor, below which clipping is the honest outcome and the author
 * should give the region more room). Runs in the browser for the canvas and the shared page, and in the
 * printed HTML before page.pdf, so the fit is identical everywhere.
 *
 * pRoot is the .bpl element (or an ancestor); pass the document to fit every layout on the page.
 */
function fitLayout(pRoot)
{
	if (!pRoot || typeof pRoot.querySelectorAll !== 'function') { return; }
	let tmpRegions = pRoot.querySelectorAll('.bpl-region');
	for (let i = 0; i < tmpRegions.length; i++)
	{
		let tmpRegion = tmpRegions[i];
		// Images and diagrams already fit by object-fit / max-height; only text regions scale their type.
		if (tmpRegion.classList.contains('bpl-region-image') || tmpRegion.classList.contains('bpl-region-diagram')) { continue; }
		let tmpContent = tmpRegion.firstElementChild;
		if (!tmpContent) { continue; }
		let tmpScale = 1;
		tmpRegion.style.setProperty('--bpl-fit', tmpScale);
		// Step down ~3% at a time to a 0.55 floor; cheap and visually smooth, and a region that still
		// overflows at the floor is a layout the author needs to resize, not a bug to hide.
		for (let tmpStep = 0; tmpStep < 24 && tmpScale > 0.55 && tmpContent.scrollHeight > tmpRegion.clientHeight + 1; tmpStep++)
		{
			tmpScale = Math.round((tmpScale - 0.03) * 1000) / 1000;
			tmpRegion.style.setProperty('--bpl-fit', tmpScale);
		}
	}
}

/**
 * Re-run fitLayout as the container resizes, so a responsive layout re-fits its text when the arrangement
 * changes -- notably when the container crosses 1:1 and regions swap to their portrait placement, which
 * re-cells every text region and makes the once-computed --bpl-fit stale. rAF-debounced; returns the
 * observer so the caller can disconnect it. A no-op where ResizeObserver is absent (and the print path,
 * which renders at one fixed sheet size, never needs it). fitLayout itself is unchanged: it resets
 * --bpl-fit to 1 per region, so a repeat call both shrinks and grows text back as room changes.
 */
function observeFit(pRoot, pTarget)
{
	if ((typeof ResizeObserver !== 'function') || !pRoot) { return null; }
	let tmpTarget = pTarget || pRoot;
	let tmpPending = 0;
	let tmpObserver = new ResizeObserver(function ()
	{
		if (tmpPending) { return; }
		tmpPending = requestAnimationFrame(function () { tmpPending = 0; fitLayout(pRoot); });
	});
	tmpObserver.observe(tmpTarget);
	return tmpObserver;
}

/**
 * A default module-overview layout from a small spec: the use case this strand exists to serve. A Retold
 * module gets a title block, its architecture diagram large on the left, a stack of "what / how / pieces"
 * blurbs down the right, and a visual example across the foot -- one arrangement, printable at any size.
 *
 * spec: { Mark, Name, Tagline, DiagramSVG, Blurbs: [{Heading, Body}], Example: { DataURI|Src, Alt, Caption } }
 */
function moduleOverview(pSpec)
{
	let tmpSpec = pSpec || {};
	// Base placements are LANDSCAPE (diagram large on the left, blurbs down the right); the Portrait alternate
	// stacks them (diagram full-width band, blurbs below) so the same sheet is beautiful on a wide monitor and
	// a narrow one. Responsive:true turns the alternates on.
	let tmpRegions =
	[
		{ Kind: 'title', Col: 1, Row: 1, ColSpan: 12, RowSpan: 2, Portrait: { Col: 1, Row: 1, ColSpan: 12, RowSpan: 2 }, Mark: tmpSpec.Mark, Name: tmpSpec.Name, Tagline: tmpSpec.Tagline },
		{ Kind: 'diagram', Col: 1, Row: 3, ColSpan: 8, RowSpan: 9, Portrait: { Col: 1, Row: 3, ColSpan: 12, RowSpan: 7 }, SVG: tmpSpec.DiagramSVG || '', Caption: tmpSpec.DiagramCaption || 'Architecture' },
		{ Kind: 'blurbs', Col: 9, Row: 3, ColSpan: 4, RowSpan: 9, Portrait: { Col: 1, Row: 10, ColSpan: 12, RowSpan: 7 }, Items: tmpSpec.Blurbs || [] }
	];
	if (tmpSpec.Example)
	{
		// Stacked with an example: title 1-2, diagram 3-9, blurbs 10-13, example 14-16 (fits 16 rows).
		tmpRegions[2].Portrait = { Col: 1, Row: 10, ColSpan: 12, RowSpan: 4 };
		tmpRegions.push({ Kind: 'image', Col: 1, Row: 12, ColSpan: 12, RowSpan: 5, Portrait: { Col: 1, Row: 14, ColSpan: 12, RowSpan: 3 },
			DataURI: tmpSpec.Example.DataURI, Src: tmpSpec.Example.Src, Alt: tmpSpec.Example.Alt, Caption: tmpSpec.Example.Caption || 'Example' });
	}
	return { Grid: { Cols: 12, Rows: 16 }, Responsive: true, Regions: tmpRegions };
}

/**
 * Compose a whole, beautiful sheet from a CONTENT spec -- the engine behind agent-authored blueprints.
 *
 * The caller supplies CONTENT, not grid cells: a title, an optional hero diagram (inline SVG line-work), a
 * stack of blurbs, an optional callout note, and an optional foot (a raster example OR a secondary diagram
 * such as a code banner). composeSheet lays them out on the 12x16 grid in balanced proportions AND emits a
 * Portrait alternate for every region, so one sheet reads side-by-side on a wide monitor and stacked on a
 * narrow one, and prints cleanly at any size. It degrades gracefully: drop the diagram and the blurbs widen
 * to fill; give only blurbs and many of them and they flow into columns (a reference "catalog"); give only a
 * diagram and it fills the body.
 *
 * spec:
 *   { Mark, Name, Tagline,                              // the title block
 *     Diagram: { SVG, Caption },                        // the hero (optional)
 *     Blurbs: [ { Heading, Body } ],                    // explanatory blocks (optional)
 *     Note: { Label, Body },                            // a callout band (optional)
 *     Image: { DataURI | Src, Alt, Caption },           // a raster example at the foot (optional)
 *     Code:  { SVG, Caption },                           // a secondary diagram / code banner at the foot (optional)
 *     Grid, Responsive, Regions }                       // Regions present => used verbatim (the power path)
 *
 * Returns a layout object ready for renderSheet / Design/Save. Kit is NOT set here -- it rides on the
 * blueprint (the caller sets layout.Kit), so one composed sheet can be drawn clean, drafting or cyanotype.
 */
function composeSheet(pSpec)
{
	let tmpSpec = pSpec || {};
	// POWER PATH: a caller who hands raw Regions gets them verbatim (responsive unless told otherwise), so an
	// author who wants exact control is never fighting the composer.
	if (Array.isArray(tmpSpec.Regions))
	{
		return { Grid: tmpSpec.Grid || { Cols: 12, Rows: 16 }, Kit: tmpSpec.Kit,
			Responsive: (tmpSpec.Responsive === false) ? false : true, Regions: tmpSpec.Regions };
	}

	let tmpBlurbs = Array.isArray(tmpSpec.Blurbs) ? tmpSpec.Blurbs.filter((pB) => pB && (pB.Heading || pB.Body)) : [];
	let tmpHasDiagram = !!(tmpSpec.Diagram && tmpSpec.Diagram.SVG);
	let tmpHasBlurbs = tmpBlurbs.length > 0;
	let tmpHasNote = !!(tmpSpec.Note && (tmpSpec.Note.Body || tmpSpec.Note.Label));
	// The foot is one full-width band: a raster example if given, else a secondary diagram (a code banner).
	let tmpFootImage = (tmpSpec.Image && (tmpSpec.Image.DataURI || tmpSpec.Image.Src)) ? tmpSpec.Image : null;
	let tmpFootCode = (!tmpFootImage && tmpSpec.Code && tmpSpec.Code.SVG) ? tmpSpec.Code : null;
	let tmpHasFoot = !!(tmpFootImage || tmpFootCode);

	// ---- Row budget (16 rows). Title owns the top two; foot the bottom, note just above it, the rest is MAIN.
	let tmpFootSpan = tmpHasFoot ? 4 : 0;
	let tmpNoteSpan = tmpHasNote ? 2 : 0;
	let tmpMainTop = 3;
	let tmpMainSpan = Math.max(2, 14 - tmpFootSpan - tmpNoteSpan);
	let tmpNoteTop = tmpMainTop + tmpMainSpan;
	let tmpFootTop = tmpNoteTop + tmpNoteSpan;

	// ---- Portrait budget: everything stacked full-width, 14 body rows shared top-to-bottom. A diagram scales to
	// whatever height it gets, so when a diagram and blurbs share the body the TEXT takes the larger share (it is
	// the thing that clips), and a code banner at the foot is thinner than a raster example.
	let tmpPFootSpan = tmpFootImage ? 4 : (tmpHasFoot ? 3 : 0);
	let tmpPBodyRows = 14 - tmpPFootSpan - tmpNoteSpan;
	let tmpPDiagramSpan = 0;
	let tmpPBlurbsSpan = 0;
	if (tmpHasDiagram && tmpHasBlurbs) { tmpPDiagramSpan = Math.floor(tmpPBodyRows * 0.45); tmpPBlurbsSpan = tmpPBodyRows - tmpPDiagramSpan; }
	else if (tmpHasDiagram) { tmpPDiagramSpan = tmpPBodyRows; }
	else if (tmpHasBlurbs) { tmpPBlurbsSpan = tmpPBodyRows; }
	let tmpPDiagramTop = 3;
	let tmpPBlurbsTop = 3 + tmpPDiagramSpan;
	let tmpPNoteTop = 3 + tmpPDiagramSpan + tmpPBlurbsSpan;
	let tmpPFootTop = tmpPNoteTop + tmpNoteSpan;

	let tmpRegions =
	[
		{ Kind: 'title', Col: 1, Row: 1, ColSpan: 12, RowSpan: 2, Portrait: { Col: 1, Row: 1, ColSpan: 12, RowSpan: 2 },
			Mark: tmpSpec.Mark, Name: tmpSpec.Name, Tagline: tmpSpec.Tagline }
	];

	if (tmpHasDiagram && tmpHasBlurbs)
	{
		// The signature arrangement: hero diagram on the left, blurbs down the right; stacked in portrait.
		tmpRegions.push({ Kind: 'diagram', Col: 1, Row: tmpMainTop, ColSpan: 8, RowSpan: tmpMainSpan,
			Portrait: { Col: 1, Row: tmpPDiagramTop, ColSpan: 12, RowSpan: tmpPDiagramSpan },
			SVG: tmpSpec.Diagram.SVG, Caption: tmpSpec.Diagram.Caption || '' });
		tmpRegions = tmpRegions.concat(_blurbColumns(tmpBlurbs, 9, tmpMainTop, 4, tmpMainSpan,
			{ Col: 1, Row: tmpPBlurbsTop, ColSpan: 12, RowSpan: tmpPBlurbsSpan }, 1));
	}
	else if (tmpHasDiagram)
	{
		tmpRegions.push({ Kind: 'diagram', Col: 1, Row: tmpMainTop, ColSpan: 12, RowSpan: tmpMainSpan,
			Portrait: { Col: 1, Row: tmpPDiagramTop, ColSpan: 12, RowSpan: tmpPDiagramSpan },
			SVG: tmpSpec.Diagram.SVG, Caption: tmpSpec.Diagram.Caption || '' });
	}
	else if (tmpHasBlurbs)
	{
		// A reference CATALOG: no hero, so the blurbs flow into up to three columns to fill the sheet (two in
		// portrait), with fitLayout sizing the type. Fewer than six blurbs stay a single comfortable column.
		let tmpCols = (tmpBlurbs.length >= 10) ? 3 : (tmpBlurbs.length >= 6) ? 2 : 1;
		tmpRegions = tmpRegions.concat(_blurbColumns(tmpBlurbs, 1, tmpMainTop, 12, tmpMainSpan,
			{ Col: 1, Row: tmpPDiagramTop, ColSpan: 12, RowSpan: tmpPBodyRows }, tmpCols));
	}

	if (tmpHasNote)
	{
		tmpRegions.push({ Kind: 'note', Col: 1, Row: tmpNoteTop, ColSpan: 12, RowSpan: tmpNoteSpan,
			Portrait: { Col: 1, Row: tmpPNoteTop, ColSpan: 12, RowSpan: tmpNoteSpan },
			Label: tmpSpec.Note.Label, Body: tmpSpec.Note.Body });
	}
	if (tmpFootImage)
	{
		tmpRegions.push({ Kind: 'image', Col: 1, Row: tmpFootTop, ColSpan: 12, RowSpan: tmpFootSpan,
			Portrait: { Col: 1, Row: tmpPFootTop, ColSpan: 12, RowSpan: tmpPFootSpan },
			DataURI: tmpFootImage.DataURI, Src: tmpFootImage.Src, Alt: tmpFootImage.Alt, Caption: tmpFootImage.Caption || '' });
	}
	else if (tmpFootCode)
	{
		tmpRegions.push({ Kind: 'diagram', Col: 1, Row: tmpFootTop, ColSpan: 12, RowSpan: tmpFootSpan,
			Portrait: { Col: 1, Row: tmpPFootTop, ColSpan: 12, RowSpan: tmpPFootSpan },
			SVG: tmpFootCode.SVG, Caption: tmpFootCode.Caption || '' });
	}

	return { Grid: { Cols: 12, Rows: 16 }, Kit: tmpSpec.Kit, Responsive: true, Regions: tmpRegions };
}

/**
 * Split a blurb list into pCols side-by-side blurbs regions across a landscape band, each carrying a Portrait
 * alternate (a single stacked column by default, or pPortrait placement for the whole set when pCols is 1).
 * Used by composeSheet for the blurbs column and the catalog archetype.
 */
function _blurbColumns(pBlurbs, pCol, pRow, pColSpan, pRowSpan, pPortrait, pCols)
{
	let tmpCols = Math.max(1, Math.min(3, Number(pCols) || 1));
	if (tmpCols === 1)
	{
		return [{ Kind: 'blurbs', Col: pCol, Row: pRow, ColSpan: pColSpan, RowSpan: pRowSpan, Portrait: pPortrait, Items: pBlurbs }];
	}
	let tmpPer = Math.ceil(pBlurbs.length / tmpCols);
	let tmpColWidth = Math.max(1, Math.floor(pColSpan / tmpCols));
	let tmpOut = [];
	for (let i = 0; i < tmpCols; i++)
	{
		let tmpItems = pBlurbs.slice(i * tmpPer, (i + 1) * tmpPer);
		if (!tmpItems.length) { continue; }
		// Portrait collapses the columns into a vertical run of two columns (six-wide each) so a narrow sheet
		// still reads top-to-bottom rather than squeezing three columns into no width.
		let tmpPortraitCol = (i % 2 === 0) ? 1 : 7;
		let tmpPortraitRowBand = Math.ceil(tmpCols / 2);
		let tmpPortraitBandRows = Math.max(2, Math.floor((pPortrait.RowSpan || 6) / tmpPortraitRowBand));
		let tmpPortraitRow = pPortrait.Row + Math.floor(i / 2) * tmpPortraitBandRows;
		tmpOut.push({ Kind: 'blurbs', Col: pCol + i * tmpColWidth, Row: pRow, ColSpan: tmpColWidth, RowSpan: pRowSpan,
			Portrait: { Col: tmpPortraitCol, Row: tmpPortraitRow, ColSpan: 6, RowSpan: tmpPortraitBandRows }, Items: tmpItems });
	}
	return tmpOut;
}

/**
 * The drawing-style KITS: one layout, different drawing styles. A kit is pure presentation -- a set of
 * CSS-variable tokens (ink, rule, panel, accent) plus a paper colour and whether the sheet wears a
 * drafting frame + title block. The same kit data drives the studio canvas, the shared web page and the
 * printed PDF, so a blueprint cannot look one way on screen and another on paper.
 *
 * 'blueprint' is cyanotype: because a diagram is inline SVG stroked in currentColor, it redraws itself in
 * the kit's ink -- white line-work on blue -- rather than carrying a baked colour.
 */
const KITS =
{
	clean:     { Label: 'Clean',     Background: '#ffffff', Frame: false,
		Tokens: { '--bpl-ink': '#12161d', '--bpl-body': '#2b3340', '--bpl-quiet': '#6b7280', '--bpl-rule': '#d6dbe4', '--bpl-panel': '#fbfcfe', '--bpl-accent': '#3b6fd4' } },
	drafting:  { Label: 'Drafting',  Background: '#f5f1e8', Frame: true,
		Tokens: { '--bpl-ink': '#232730', '--bpl-body': '#2d343d', '--bpl-quiet': '#6b7480', '--bpl-rule': '#b2ab98', '--bpl-panel': 'transparent', '--bpl-accent': '#232730', '--tb-bg': '#f5f1e8' } },
	blueprint: { Label: 'Blueprint', Background: '#15325f', Frame: true,
		Tokens: { '--bpl-ink': '#eaf3ff', '--bpl-body': '#d2e2f6', '--bpl-quiet': '#9ec0ec', '--bpl-rule': 'rgba(255,255,255,.42)', '--bpl-panel': 'rgba(255,255,255,.05)', '--bpl-accent': '#8fd3ff', '--tb-bg': '#15325f' } }
};
function kitOf(pKit) { return KITS[pKit] || KITS.clean; }

// The inline style that paints a sheet in a kit: the paper colour plus the kit's tokens. Applied to the
// .bpl-sheet element, so the kit travels with the markup (no per-kit stylesheet to keep in sync).
function kitStyle(pKit)
{
	let tmpKit = kitOf(pKit);
	let tmpStyle = 'background:' + tmpKit.Background + ';';
	for (let tmpToken in tmpKit.Tokens) { tmpStyle += tmpToken + ':' + tmpKit.Tokens[tmpToken] + ';'; }
	return tmpStyle;
}

// The drafting title block, drawn from the layout's own title region so the corner block and the sheet
// agree on what this is. Shown only for kits that wear a frame.
function _titleBlock(pLayout, pOptions)
{
	let tmpOptions = pOptions || {};
	let tmpTitle = ((pLayout || {}).Regions || []).find((pR) => pR.Kind === 'title') || {};
	// A fourth PAGE cell appears only when this sheet is rendered as one page of a set (a deck passes
	// Page: {Index, Total}), so a standalone blueprint keeps its three-cell block exactly as before.
	let tmpPageCell = '';
	if (tmpOptions.Page && Number(tmpOptions.Page.Total) > 0)
	{
		tmpPageCell = '<div class="bpl-tb-cell"><div class="bpl-tb-k">Page</div><div class="bpl-tb-v">'
			+ _escape((Number(tmpOptions.Page.Index) || 1) + ' of ' + Number(tmpOptions.Page.Total)) + '</div></div>';
	}
	return '<div class="bpl-tb' + (tmpPageCell ? ' bpl-tb-wide' : '') + '">'
		+ '<div class="bpl-tb-name">' + _escape(tmpTitle.Name || 'Untitled') + '</div>'
		+ '<div class="bpl-tb-row">'
			+ '<div class="bpl-tb-cell"><div class="bpl-tb-k">Sheet</div><div class="bpl-tb-v">' + _escape(tmpTitle.Mark || 'BP-001') + '</div></div>'
			+ '<div class="bpl-tb-cell"><div class="bpl-tb-k">Scale</div><div class="bpl-tb-v">NTS</div></div>'
			+ '<div class="bpl-tb-cell"><div class="bpl-tb-k">Rev</div><div class="bpl-tb-v">A</div></div>'
			+ tmpPageCell
		+ '</div></div>';
}

// The per-plan-sheet brand band: an uploaded logo + a short footer line, stamped at render time on EVERY
// sheet. The brand is never stored in the layout; it rides in as pOptions.Brand ({ LogoDataURI, Footer })
// resolved by the endpoint from the tenant, so a logo change re-brands existing blueprints. LogoDataURI is
// a self-contained data: URI (the login-free public page and headless-Chromium PDF cannot fetch an authed
// Media URL), so the endpoint inlines the bytes. Returns '' when the tenant has no brand set.
function _brandBand(pBrand)
{
	let tmpBrand = pBrand || {};
	let tmpURI = String(tmpBrand.LogoDataURI || '');
	let tmpFoot = String(tmpBrand.Footer || '');
	let tmpLogo = tmpURI ? ('<img class="bpl-brand-logo" alt="" src="' + _escape(tmpURI) + '">') : '';
	let tmpFootHTML = tmpFoot ? ('<span class="bpl-brand-foot">' + _escape(tmpFoot) + '</span>') : '';
	if (!tmpLogo && !tmpFootHTML) { return ''; }
	return '<div class="bpl-brand">' + tmpLogo + tmpFootHTML + '</div>';
}

/**
 * A whole sheet: the layout grid wrapped in a kit. This is the ONE render the web page and the print path
 * both use; the studio canvas uses it too, then overlays its editing chrome. The kit rides on the element
 * (data-bpl-kit + inline tokens), so there is nothing to theme separately.
 *
 * pOptions.Kit    a key from KITS (default 'clean').
 * pOptions.Brand  optional { LogoDataURI, Footer } -- the tenant's brand band, stamped on this sheet.
 */
function renderSheet(pLayout, pOptions)
{
	let tmpOptions = pOptions || {};
	let tmpKey = tmpOptions.Kit || (pLayout && pLayout.Kit) || 'clean';
	let tmpKit = kitOf(tmpKey);
	let tmpBrandBand = _brandBand(tmpOptions.Brand);
	return '<div class="bpl-sheet' + (tmpBrandBand ? ' bpl-sheet-branded' : '') + '" data-bpl-kit="' + _escape(tmpKey) + '" style="' + kitStyle(tmpKey) + '">'
		+ (tmpKit.Frame ? '<div class="bpl-frame"></div>' : '')
		+ renderLayout(pLayout, tmpOptions)
		+ (tmpKit.Frame ? _titleBlock(pLayout, tmpOptions) : '')
		+ tmpBrandBand
	+ '</div>';
}

// The kit chrome (sheet wrapper, frame, title block) + the kit overrides. Sizes that must scale with the
// paper are in container units off .bpl-sheet, so the title block is proportional on a web card and on a
// 36in sheet alike. Pair this with LAYOUT_CSS; SHEET_CSS is both, for a caller that wants one string.
const KIT_CSS = /*css*/`
.bpl-sheet { position: relative; width: 100%; height: 100%; box-sizing: border-box; container-type: size; padding: 3.2%; }
/* inset/right/bottom in cqw (container WIDTH) on every side, not %, because a % inset is width-relative for
   left/right but height-relative for top/bottom -- so on a non-square sheet equal percentages give unequal
   pixel gaps and the frame (and the title block) sits closer to the top/bottom than the sides. cqw keeps all
   four margins equal, matching the content grid, whose padding % is already width-based. */
.bpl-frame { position: absolute; inset: 1.65cqw; border: 0.12cqw solid var(--bpl-ink, #222); pointer-events: none; z-index: 3; }
.bpl-frame::after { content: ''; position: absolute; inset: 0.5cqw; border: 0.06cqw solid var(--bpl-rule, #999); }
/* The title block is a quiet corner reference, not a headline: a modest cartouche that stays out of the
   content's way. Sizes are in cqw so it stays the same proportion of the sheet from a web card to a 36in
   print, and deliberately small -- the sheet name here should never be the largest type on the page. */
.bpl-tb { position: absolute; right: 2.75cqw; bottom: 2.75cqw; width: 20%; z-index: 4; border: 0.08cqw solid var(--bpl-ink, #222); background: var(--tb-bg, #f5f1e8); font-family: "IBM Plex Mono", monospace; color: var(--bpl-ink, #222); }
/* A set page carries a fourth PAGE cell, so its block is a touch wider to keep every cell legible. */
.bpl-tb-wide { width: 26%; }
.bpl-tb-name { padding: 0.7cqw 1.1cqw; border-bottom: 0.06cqw solid var(--bpl-ink, #222); font-weight: 700; font-size: 1.2cqw; letter-spacing: .05em; text-transform: uppercase; }
.bpl-tb-row { display: flex; }
.bpl-tb-cell { flex: 1; padding: 0.55cqw 1.1cqw; border-right: 0.05cqw solid var(--bpl-rule, #999); }
.bpl-tb-cell:last-child { border-right: 0; }
.bpl-tb-k { font-size: 0.72cqw; letter-spacing: .08em; color: var(--bpl-quiet, #777); text-transform: uppercase; margin-bottom: 0.3cqw; }
.bpl-tb-v { font-size: 0.92cqw; }
/* The brand band. A branded sheet reserves a bottom strip (extra padding-bottom, width-relative like the
   base 3.2% padding) and the band is placed absolutely inside it, so the content grid never overlaps it and
   the change touches ONLY sheets that carry a brand. Capped to the left 68% so it clears the framed-kit title
   block (bottom-right). The footer ink is var(--bpl-quiet), which the print media query below darkens on the
   white framed-kit print palette, so it stays legible in the PDF. */
.bpl-sheet-branded { padding-bottom: 8%; }
.bpl-brand { position: absolute; left: 3.2%; bottom: 1.5%; max-width: 68%; height: 4.6cqw; display: flex; align-items: center; gap: 1.3cqw; z-index: 4; }
.bpl-brand-logo { height: 100%; max-height: 4.6cqw; width: auto; object-fit: contain; }
.bpl-brand-foot { font-family: "IBM Plex Mono", monospace; font-size: 0.95cqw; letter-spacing: .04em; color: var(--bpl-quiet, #777); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bpl-sheet[data-bpl-kit="drafting"] .bpl-region, .bpl-sheet[data-bpl-kit="blueprint"] .bpl-region,
.bpl-sheet[data-bpl-kit="drafting"] .bpl-diagram, .bpl-sheet[data-bpl-kit="blueprint"] .bpl-diagram,
.bpl-sheet[data-bpl-kit="drafting"] .bpl-image img, .bpl-sheet[data-bpl-kit="blueprint"] .bpl-image img,
.bpl-sheet[data-bpl-kit="drafting"] .bpl-image-empty, .bpl-sheet[data-bpl-kit="blueprint"] .bpl-image-empty { border-radius: 0 !important; }
.bpl-sheet[data-bpl-kit="drafting"] .bpl-diagram, .bpl-sheet[data-bpl-kit="blueprint"] .bpl-diagram { background: transparent; border-width: 0.12cqh; border-color: var(--bpl-rule); }
.bpl-sheet[data-bpl-kit="drafting"] .bpl-title-name, .bpl-sheet[data-bpl-kit="blueprint"] .bpl-title-name { text-transform: uppercase; letter-spacing: .015em; }
/* PRINT: the cyanotype navy (and the cream drafting paper) are a screen treatment. On paper they waste ink and
   read poorly, so a printed sheet drops to white with dark ink -- the clean palette. The !important is deliberate:
   the kit paints each sheet with an inline background and token set that a plain rule could not override. The
   diagram accent hues (the purple mark, the cyan rules) stay as drawn, which reads fine on white. */
@media print {
	.bpl-sheet[data-bpl-kit="blueprint"], .bpl-sheet[data-bpl-kit="drafting"] {
		background: #ffffff !important; color: #12161d !important;
		--bpl-ink: #12161d !important; --bpl-body: #2b3340 !important; --bpl-quiet: #5a6472 !important;
		--bpl-rule: #c2c9d4 !important; --bpl-panel: #ffffff !important; --bpl-accent: #2f5fb0 !important;
		--tb-bg: #ffffff !important;
	}
	/* Firefox does not carry the ink TOKEN into an SVG's currentColor when printing, so the inline diagram
	   line-work and labels keep the pale on-screen ink and vanish on white (Chrome carries it fine). Set the
	   colour LITERALLY on the grid and the diagram SVGs -- not through the var -- so currentColor resolves dark
	   in every browser. */
	.bpl-sheet[data-bpl-kit="blueprint"] .bpl, .bpl-sheet[data-bpl-kit="drafting"] .bpl,
	.bpl-sheet[data-bpl-kit="blueprint"] .bpl-diagram svg, .bpl-sheet[data-bpl-kit="drafting"] .bpl-diagram svg {
		color: #12161d !important;
	}
}
`;
const SHEET_CSS = LAYOUT_CSS + KIT_CSS;

// The kits as a list, for a picker (studio control / settings).
function kitList() { return Object.keys(KITS).map((pK) => ({ Key: pK, Label: KITS[pK].Label, Frame: KITS[pK].Frame })); }

module.exports = {
	renderLayout: renderLayout, moduleOverview: moduleOverview, fitLayout: fitLayout, observeFit: observeFit,
	renderSheet: renderSheet, kitStyle: kitStyle, kitList: kitList, KITS: KITS, composeSheet: composeSheet,
	LAYOUT_CSS: LAYOUT_CSS, KIT_CSS: KIT_CSS, SHEET_CSS: SHEET_CSS
};
